/**
 * Client: FieldConnect-Bestellung an Shopify senden (Versand).
 * Die eigentliche Arbeit macht /api/shopify-order-push (serverseitig,
 * idempotent). Fehler blockieren nie das Anlegen einer Bestellung.
 */
import { toast } from 'sonner';
import { getAccessToken } from '@/api/adapters/SupabaseAdapter';

export function isInShopify(order) {
  return String(order?.shopify_mirror_id || '').startsWith('gid://');
}

/**
 * @param {string} orderId
 * @param {{ force?: boolean }} [opts] force: Admin sendet eine eigene Zustellung bewusst doch
 * @returns {Promise<{ok: boolean, name?: string, already?: boolean, skipped?: string, hasAddress?: boolean, error?: string}>}
 */
export async function pushOrderToShopify(orderId, { force = false } = {}) {
  if (!orderId) return { ok: false, error: 'Keine Bestellung' };
  try {
    const token = await getAccessToken();
    const r = await fetch('/api/shopify-order-push', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ orderId, ...(force ? { force: true } : {}) }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) return { ok: false, error: data.error || `HTTP ${r.status}` };
    return { ok: true, ...data };
  } catch (err) {
    return { ok: false, error: err.message };
  }
}

/**
 * Im Hintergrund senden und das Ergebnis als Toast melden.
 * @param {string} orderId
 * @param {{ orderNumber?: string, silentSuccess?: boolean, force?: boolean, onDone?: () => void }} [opts]
 */
export function pushOrderInBackground(orderId, opts = {}) {
  const label = opts.orderNumber ? `${opts.orderNumber} ` : '';
  pushOrderToShopify(orderId, { force: opts.force }).then((r) => {
    if (r.ok && r.skipped === 'eigene_zustellung') {
      if (!opts.silentSuccess) toast.info(`${label}Eigene Zustellung — nicht an Shopify gesendet (kein DPD-Etikett).`);
    } else if (!r.ok) {
      toast.error(`${label}nicht an Shopify übertragen: ${r.error}. Unter Aufträge erneut senden.`);
    } else if (!opts.silentSuccess && !r.already) {
      toast.success(`${label}in Shopify angelegt${r.name ? ` (${r.name})` : ''}`);
      if (r.hasAddress === false) toast.warning(`${label}ohne Lieferadresse in Shopify — bitte Adresse beim Kunden ergänzen`);
    }
    opts.onDone?.();
  });
}
